import { isVoid } from '@lglp/utils';
import {
  __,
  curry,
  mergeAll,
  reduce,
  prop,
  compose,
  split,
  length,
  match,
  propOr,
  map,
  test,
} from 'ramda';
import { generatePath } from 'react-router';

import { REDIRECT_ROUTES } from '../../config/routes/redirect';
import { asyncComponent } from '../../routing/AsyncComponent';
import { FR, EN, getLang } from '../i18n';

/**
 * Add children routes to route list
 * @param {Array} routes
 * @param {Array} parentRoute (optional)
 * @returns {Array}
 */
const addChildrenRoutes = (routes, parentRoute) =>
  reduce(
    (acc, { children = [], ...route }) => {
      const r = parentRoute
        ? {
            ...route,
            id: `${parentRoute.id}__${route.id}`,
            fr: `${parentRoute.fr}/${route.fr}`,
            en: `${parentRoute.en}/${route.en}`,
            exact: true,
          }
        : { ...route, exact: !length(children) };
      return [...acc, ...addChildrenRoutes(children, r), r];
    },
    [],
    routes
  );

/**
 * Add a single route
 * @param {String} lng
 * @param {Object} route
 * @param {Object} configuration of all routes
 * @returns {Object} the new configuration
 */
const addRoute = (lng, route, { PATH_TO_ROUTE, ROUTES, ROUTE_TO_PATH }) => {
  let path = `/:lng(${lng})`;
  if (!isVoid(prop(lng, route))) {
    path = `${path}/${prop(lng, route)}`;
  }

  return {
    PATH_TO_ROUTE: {
      ...PATH_TO_ROUTE,
      [path]: route.id,
    },
    ROUTES: [
      ...ROUTES,
      {
        id: route.id,
        path,
        exact: route.exact,
        component: asyncComponent({
          loader: route.fileLoader,
        }),
      },
    ],
    ROUTE_TO_PATH: {
      ...ROUTE_TO_PATH,
      [route.id]: {
        ...ROUTE_TO_PATH[route.id],
        [lng]: path,
      },
    },
  };
};

/**
 * Function to get a not-found route definition with the given props
 * @param {Object} configuration of all routes
 * @return {Object} the new configuration with error route
 */
const addNotFoundErrorRoute = ({ ROUTES, ...config }) => ({
  ...config,
  ROUTES: [
    ...ROUTES,
    {
      id: 'error',
      path: '',
      component: asyncComponent({
        loader: () => import('../../pages/ErrorPage'),
      }),
      httpStatusCode: 404,
    },
  ],
});

const addRedirectRoutes = ({ ROUTES, ...config }) => ({
  ...config,
  ROUTES: [...REDIRECT_ROUTES, ...ROUTES],
});

const makeRouteConfig = reduce(
  ({ ROUTE_KEY, ...acc }, route) => {
    let res = acc;
    if (route.fr !== undefined) {
      res = addRoute(FR, route, res);
    }
    if (route.en !== undefined) {
      res = addRoute(EN, route, res);
    }
    return {
      ...res,
      ROUTE_KEY: { ...ROUTE_KEY, [route.id]: route.id },
    };
  },
  { ROUTE_KEY: {}, PATH_TO_ROUTE: {}, ROUTE_TO_PATH: {}, ROUTES: [] }
);

const extractDefaultParams = compose(
  mergeAll,
  map(
    compose(
      ([, key, value]) => key && { [key]: value },
      match(/^:([\w_-]+)(?:\(([\w_-]+)\))?\??/)
    )
  ),
  split('/')
);

const appendChildKey = curry((childKey, routeKey) => {
  if (!test(new RegExp(`/__${childKey}$/`), routeKey)) {
    return `${routeKey}__${childKey}`;
  }
  return routeKey;
});

export const setupRoutes = (routes) => {
  const { ROUTE_KEY, ROUTES, PATH_TO_ROUTE, ROUTE_TO_PATH } = compose(
    addRedirectRoutes,
    addNotFoundErrorRoute,
    makeRouteConfig,
    addChildrenRoutes
  )(routes);

  const getRouteKeyForPath = prop(__, PATH_TO_ROUTE);

  const getUrlForRouteKey = (routeKey, lng, params = {}) => {
    const lang = getLang(lng);
    const path = ROUTE_TO_PATH[routeKey]?.[lang];

    if (path) {
      return generatePath(path, {
        ...extractDefaultParams(path),
        ...params,
        lng: lang,
      });
    }
  };

  const getRouteKeyOfChild = (defaultRouteKey, childKey, routeKey) =>
    compose(
      propOr(appendChildKey(childKey, defaultRouteKey), __, ROUTE_KEY),
      appendChildKey(childKey)
    )(routeKey);

  return {
    ROUTE_KEY,
    ROUTES,
    getRouteKeyForPath,
    getUrlForRouteKey,
    getRouteKeyOfChild,
  };
};
